import { apiRequest, ApiError, resolveApiUrl } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";
import { normalizeHiddenClinics, normalizeHiddenMenuItems, normalizePermissionsMap } from "@/lib/permissions";

// Team management API (Settings → Team): the employee directory + RBAC permission groups.
// Kept out of admin.js — that module is patient/thread-centric and already ~950 lines;
// these endpoints form one cohesive staff-management domain.

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function normalizeStringList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => String(item || "").trim()).filter(Boolean);
}

function normalizeAwards(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      title: String(item?.title || "").trim(),
      note: String(item?.note || "").trim(),
    }))
    .filter((item) => item.title);
}

// Contact lists (detail-only, like the bio). `verified` is server-owned state the editor
// displays but never sets — the server preserves it for entries whose value is unchanged.
function normalizeStaffPhones(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      number: String(item?.number || "").trim(),
      label: String(item?.label || "").trim(),
      verified: Boolean(item?.verified),
    }))
    .filter((item) => item.number);
}

function normalizeStaffEmails(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      email: String(item?.email || "").trim(),
      verified: Boolean(item?.verified),
    }))
    .filter((item) => item.email);
}

// Request shapes: the server reads only number/label/email, so the verified flag (and any
// other UI-side state) is stripped rather than echoed back.
function staffPhonesPayload(phones) {
  if (!Array.isArray(phones)) return [];
  return phones.map((item) => ({
    number: String(item?.number || "").trim(),
    label: String(item?.label || "").trim(),
  }));
}

function staffEmailsPayload(emails) {
  if (!Array.isArray(emails)) return [];
  return emails.map((item) => ({ email: String(item?.email || "").trim() }));
}

function normalizeStaffUser(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: String(raw.id || ""),
    tenantId: String(raw.tenantId || ""),
    loginId: String(raw.loginId || ""),
    userType: String(raw.userType || ""),
    // Work-plan directory grouping — one of the configured work-role ids; "" when unset.
    workRole: String(raw.workRole || ""),
    // Hidden from the Employees list and the Work Plan roster (presentation only).
    hiddenFromStaffList: Boolean(raw.hiddenFromStaffList),
    status: String(raw.status || ""),
    isAdmin: Boolean(raw.isAdmin),
    permissionGroupIds: Array.isArray(raw.permissionGroupIds)
      ? raw.permissionGroupIds.map((id) => String(id))
      : [],
    teamIds: Array.isArray(raw.teamIds) ? raw.teamIds.map((id) => String(id)) : [],
    displayName: String(raw.displayName || ""),
    firstName: String(raw.firstName || ""),
    lastName: String(raw.lastName || ""),
    profile: raw.profile && typeof raw.profile === "object" ? raw.profile : null,
    // The server returns an API-RELATIVE ticket path; a plain <img> would resolve it against the
    // renderer origin (file:// or the Vite dev server), which is never the API.
    avatarUrl: resolveApiUrl(raw.avatarUrl),
    hasAvatar: Boolean(raw.hasAvatar),
    hasBio: Boolean(raw.hasBio),
    // Detail-only: list rows omit both (a bio photo is ~300 kB of base64) and carry hasBio instead.
    bio: String(raw.bio || ""),
    bioPhoto: String(raw.bioPhoto || ""),
    // Structured profile block — detail-only, like the bio. Normalized to concrete types so the
    // editor never has to guard against a missing key.
    headline: String(raw.headline || ""),
    specialties: normalizeStringList(raw.specialties ?? raw.profile?.specialties),
    qualifications: normalizeStringList(raw.qualifications),
    clinicalInterests: normalizeStringList(raw.clinicalInterests),
    languages: normalizeStringList(raw.languages),
    quote: String(raw.quote || ""),
    awards: normalizeAwards(raw.awards),
    // Contact details (contacts.{phones,emails}) — detail-only, [] on list rows.
    phones: normalizeStaffPhones(raw.phones),
    emails: normalizeStaffEmails(raw.emails),
    // Directory facets (metadata._index / metadata.childrensCare) — present on list rows too.
    // null = unordered (the app lists those last, alphabetically).
    orderIndex: Number.isFinite(raw.orderIndex) ? Math.trunc(raw.orderIndex) : null,
    childrensCare: Boolean(raw.childrensCare),
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  };
}

function normalizePermissionGroup(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: String(raw.id || ""),
    tenantId: String(raw.tenantId || ""),
    name: String(raw.name || ""),
    description: String(raw.description || ""),
    permissions: normalizePermissionsMap(raw.permissions),
    hiddenMenuItems: normalizeHiddenMenuItems(raw.hiddenMenuItems),
    hiddenClinics: normalizeHiddenClinics(raw.hiddenClinics),
    // Opt-in facet — default HIDDEN, so only an explicit true counts (never `!== false`:
    // groups created before the facet must stay hidden from the chat directory).
    chatDirectoryVisible: raw.chatDirectoryVisible === true,
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  };
}

/**
 * GET /api/admin/staff — tenant employee directory (staff:view). Includes inactive accounts
 * unless `status` narrows them out. `userType` narrows to coordinator|doctor, `permissionGroupId`
 * to one access group and `teamId` to one routing team. Every filter is applied SERVER-side, so
 * the returned `total` matches the visible rows across pages. `sort` is one of
 * name|login|role|status|updated and `dir` asc|desc; the server whitelists both and falls back to
 * name-ascending.
 */
export async function fetchStaff({ token, search, status, userType, permissionGroupId, teamId, sort, dir, skip, limit, includeHidden } = {}) {
  const query = new URLSearchParams();
  const normalizedSearch = String(search || "").trim();
  if (normalizedSearch) query.set("search", normalizedSearch);
  const normalizedStatus = String(status || "").trim();
  if (normalizedStatus) query.set("status", normalizedStatus);
  const normalizedUserType = String(userType || "").trim();
  if (normalizedUserType) query.set("userType", normalizedUserType);
  const normalizedGroupId = String(permissionGroupId || "").trim();
  if (normalizedGroupId) query.set("permissionGroupId", normalizedGroupId);
  const normalizedTeamId = String(teamId || "").trim();
  if (normalizedTeamId) query.set("teamId", normalizedTeamId);
  const normalizedSort = String(sort || "").trim();
  if (normalizedSort) query.set("sort", normalizedSort);
  const normalizedDir = String(dir || "").trim();
  if (normalizedDir) query.set("dir", normalizedDir);
  if (Number.isFinite(skip) && skip > 0) query.set("skip", String(skip));
  if (Number.isFinite(limit) && limit > 0) query.set("limit", String(limit));
  // Hidden employees are excluded server-side unless asked for — that is the flag's point.
  if (includeHidden) query.set("includeHidden", "true");

  const path = query.toString() ? `/api/admin/staff?${query.toString()}` : "/api/admin/staff";
  const payload = await apiRequest(path, { method: "GET", headers: createAuthHeaders(token) });

  return {
    items: Array.isArray(payload?.items) ? payload.items.map(normalizeStaffUser).filter(Boolean) : [],
    total: Number(payload?.total) || 0,
    raw: payload,
  };
}

/**
 * GET /api/admin/staff/{userId} — one employee at full fidelity (staff:view). The LIST rows omit
 * the inline avatar, the bio and the bio photo to keep the directory payload small, so the editor
 * hydrates from here before it can safely save profile fields.
 */
export async function fetchStaffUser({ token, userId } = {}) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) throw new ApiError("userId is required", 400);
  const payload = await apiRequest(`/api/admin/staff/${encodeURIComponent(normalizedUserId)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
  });
  return normalizeStaffUser(payload);
}

/**
 * POST /api/admin/staff — create an employee (staff:manage; isAdmin requires an admin caller).
 * Profile fields ride along, so a new employee lands complete in one request. `photoUrl`/`bioPhoto`
 * are data URIs; the server re-encodes them (512px and 1024px JPEG respectively).
 */
export async function createStaff({ token, loginId, password, displayName, userType, workRole, hiddenFromStaffList, status, isAdmin, permissionGroupIds, teamIds, firstName, lastName, photoUrl, bio, bioPhoto, headline, specialties, qualifications, clinicalInterests, languages, quote, awards, orderIndex, childrensCare, phones, emails } = {}) {
  const normalizedLoginId = String(loginId || "").trim();
  if (!normalizedLoginId) throw new ApiError("loginId is required", 400);
  if (!String(password || "").trim()) throw new ApiError("password is required", 400);

  const payload = await apiRequest("/api/admin/staff", {
    method: "POST",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({
      loginId: normalizedLoginId,
      password,
      displayName: String(displayName || "").trim(),
      userType: String(userType || "").trim(),
      workRole: String(workRole || "").trim(),
      hiddenFromStaffList: Boolean(hiddenFromStaffList),
      status: String(status || "").trim(),
      isAdmin: Boolean(isAdmin),
      permissionGroupIds: Array.isArray(permissionGroupIds) ? permissionGroupIds : [],
      teamIds: Array.isArray(teamIds) ? teamIds : [],
      firstName: String(firstName || "").trim(),
      lastName: String(lastName || "").trim(),
      photoUrl: String(photoUrl || ""),
      bio: String(bio || ""),
      bioPhoto: String(bioPhoto || ""),
      headline: String(headline || "").trim(),
      specialties: normalizeStringList(specialties),
      qualifications: normalizeStringList(qualifications),
      clinicalInterests: normalizeStringList(clinicalInterests),
      languages: normalizeStringList(languages),
      quote: String(quote || "").trim(),
      awards: normalizeAwards(awards),
      // orderIndex travels as a decimal STRING so "" can mean "don't set" (server contract).
      orderIndex: String(orderIndex ?? "").trim(),
      childrensCare: Boolean(childrensCare),
      phones: staffPhonesPayload(phones),
      emails: staffEmailsPayload(emails),
    }),
  });
  return normalizeStaffUser(payload);
}

/**
 * PUT /api/admin/staff/{userId} — partial employee patch (staff:manage). Only defined fields
 * are sent; a non-empty `password` resets credentials. isAdmin changes and edits to admin
 * accounts are rejected server-side unless the caller is an admin.
 *
 * `loginId` renames the sign-in credential. Unlike the profile fields below it is NOT three-state:
 * the server rejects a blank one (400) rather than clearing it, and a login ID already taken in
 * the tenant comes back as 409. Send it only when it actually changed — the server lowercases it,
 * so an echo of the stored value is a no-op but a needless collision check.
 *
 * Profile fields are three-state, and the distinction is load-bearing: leaving one `undefined`
 * keeps the stored value, while sending "" CLEARS it. Never pass a blank string for a field the
 * caller has not actually loaded — that deletes data.
 *
 * `orderIndex` follows the same contract as a decimal STRING ("" unsets metadata._index).
 * `childrensCare` is a definitive boolean whenever sent (false unsets the flag server-side).
 *
 * `phones`/`emails` REPLACE the stored contact list wholesale when sent ([] clears it) — unlike
 * the profile fields there is no per-entry patch, so always send the full edited list.
 */
export async function updateStaff({ token, userId, loginId, displayName, userType, workRole, hiddenFromStaffList, status, isAdmin, permissionGroupIds, teamIds, password, firstName, lastName, photoUrl, bio, bioPhoto, headline, specialties, qualifications, clinicalInterests, languages, quote, awards, orderIndex, childrensCare, phones, emails } = {}) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) throw new ApiError("userId is required", 400);

  const body = {};
  if (loginId !== undefined) {
    const normalizedLoginId = String(loginId || "").trim();
    if (!normalizedLoginId) throw new ApiError("loginId cannot be empty", 400);
    body.loginId = normalizedLoginId;
  }
  if (displayName !== undefined) body.displayName = String(displayName || "").trim();
  if (firstName !== undefined) body.firstName = String(firstName || "").trim();
  if (lastName !== undefined) body.lastName = String(lastName || "").trim();
  if (photoUrl !== undefined) body.photoUrl = String(photoUrl || "");
  if (bio !== undefined) body.bio = String(bio || "");
  if (bioPhoto !== undefined) body.bioPhoto = String(bioPhoto || "");
  if (headline !== undefined) body.headline = String(headline || "").trim();
  if (quote !== undefined) body.quote = String(quote || "").trim();
  if (specialties !== undefined) body.specialties = normalizeStringList(specialties);
  if (qualifications !== undefined) body.qualifications = normalizeStringList(qualifications);
  if (clinicalInterests !== undefined) body.clinicalInterests = normalizeStringList(clinicalInterests);
  if (languages !== undefined) body.languages = normalizeStringList(languages);
  if (awards !== undefined) body.awards = normalizeAwards(awards);
  if (orderIndex !== undefined) body.orderIndex = String(orderIndex ?? "").trim();
  if (childrensCare !== undefined) body.childrensCare = Boolean(childrensCare);
  if (phones !== undefined) body.phones = staffPhonesPayload(phones);
  if (emails !== undefined) body.emails = staffEmailsPayload(emails);
  if (userType !== undefined) body.userType = String(userType || "").trim();
  // Three-state like the profile fields: undefined keeps, "" clears, a value sets.
  if (workRole !== undefined) body.workRole = String(workRole || "").trim();
  if (hiddenFromStaffList !== undefined) body.hiddenFromStaffList = Boolean(hiddenFromStaffList);
  if (status !== undefined) body.status = String(status || "").trim();
  if (isAdmin !== undefined) body.isAdmin = Boolean(isAdmin);
  if (permissionGroupIds !== undefined) {
    body.permissionGroupIds = Array.isArray(permissionGroupIds) ? permissionGroupIds : [];
  }
  if (teamIds !== undefined) {
    body.teamIds = Array.isArray(teamIds) ? teamIds : [];
  }
  if (password !== undefined && String(password || "").trim()) body.password = password;

  const payload = await apiRequest(`/api/admin/staff/${encodeURIComponent(normalizedUserId)}`, {
    method: "PUT",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return normalizeStaffUser(payload);
}

/**
 * GET /api/teams — the routing teams directory (any authenticated staff). Backs the employee
 * editor's team assignment; membership itself is written via createStaff/updateStaff teamIds.
 *
 * `q` narrows by name SERVER-side — the endpoint caps at 50 results, and real databases carry
 * many auto-created (often nameless) patient care teams, so the picker must search rather
 * than assume one fetch sees everything. Nameless teams get their _id as the display name
 * (never a blank row), and patient-scoped ones a hint so they're tellable from routing teams.
 * Named teams sort first.
 */
export async function fetchTeams({ token, q } = {}) {
  const query = new URLSearchParams();
  const normalizedQuery = String(q || "").trim();
  if (normalizedQuery) query.set("q", normalizedQuery);
  const path = query.toString() ? `/api/teams?${query.toString()}` : "/api/teams";
  const payload = await apiRequest(path, { method: "GET", headers: createAuthHeaders(token) });
  const items = Array.isArray(payload?.teams)
    ? payload.teams
        .map((t) => {
          const id = String(t?.id || "");
          const name = String(t?.name || "").trim();
          const patientScoped = Boolean(t?.patientId) || String(t?.scope || "") === "patient";
          return {
            id,
            name: name || id,
            hasName: Boolean(name),
            description: patientScoped ? "Patient care team" : "",
            memberCount: Array.isArray(t?.members) ? t.members.length : 0,
          };
        })
        .filter((t) => t.id)
        .sort((a, b) => {
          if (a.hasName !== b.hasName) return a.hasName ? -1 : 1;
          return a.name.localeCompare(b.name);
        })
    : [];
  return { items, raw: payload };
}

/**
 * GET /api/teams/{teamId} — one team by id. Used to resolve names of an employee's selected
 * teams when they fall outside the directory's 50-result search window.
 */
export async function fetchTeam({ token, teamId } = {}) {
  const normalizedTeamId = String(teamId || "").trim();
  if (!normalizedTeamId) throw new ApiError("teamId is required", 400);
  const payload = await apiRequest(`/api/teams/${encodeURIComponent(normalizedTeamId)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
  });
  const id = String(payload?.id || normalizedTeamId);
  const name = String(payload?.name || "").trim();
  const patientScoped = Boolean(payload?.patientId) || String(payload?.scope || "") === "patient";
  return {
    id,
    name: name || id,
    hasName: Boolean(name),
    description: patientScoped ? "Patient care team" : "",
    memberCount: Array.isArray(payload?.members) ? payload.members.length : 0,
  };
}

/** GET /api/admin/permission-groups — the tenant's access groups (staff:view). */
export async function fetchPermissionGroups({ token } = {}) {
  const payload = await apiRequest("/api/admin/permission-groups", {
    method: "GET",
    headers: createAuthHeaders(token),
  });
  return {
    items: Array.isArray(payload?.items)
      ? payload.items.map(normalizePermissionGroup).filter(Boolean)
      : [],
    raw: payload,
  };
}

/** POST /api/admin/permission-groups — create a group (staff:manage). */
export async function createPermissionGroup({ token, name, description, permissions, hiddenMenuItems, hiddenClinics, chatDirectoryVisible } = {}) {
  const payload = await apiRequest("/api/admin/permission-groups", {
    method: "POST",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: String(name || "").trim(),
      description: String(description || "").trim(),
      permissions: normalizePermissionsMap(permissions),
      hiddenMenuItems: normalizeHiddenMenuItems(hiddenMenuItems),
      hiddenClinics: normalizeHiddenClinics(hiddenClinics),
      chatDirectoryVisible: chatDirectoryVisible === true,
    }),
  });
  return normalizePermissionGroup(payload);
}

/** PUT /api/admin/permission-groups/{groupId} — replace the group's editable fields (staff:manage). */
export async function updatePermissionGroup({ token, groupId, name, description, permissions, hiddenMenuItems, hiddenClinics, chatDirectoryVisible } = {}) {
  const normalizedGroupId = String(groupId || "").trim();
  if (!normalizedGroupId) throw new ApiError("groupId is required", 400);

  const payload = await apiRequest(`/api/admin/permission-groups/${encodeURIComponent(normalizedGroupId)}`, {
    method: "PUT",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: String(name || "").trim(),
      description: String(description || "").trim(),
      permissions: normalizePermissionsMap(permissions),
      hiddenMenuItems: normalizeHiddenMenuItems(hiddenMenuItems),
      hiddenClinics: normalizeHiddenClinics(hiddenClinics),
      chatDirectoryVisible: chatDirectoryVisible === true,
    }),
  });
  return normalizePermissionGroup(payload);
}

/** DELETE /api/admin/permission-groups/{groupId} — soft delete; referencing users just lose its rights. */
export async function deletePermissionGroup({ token, groupId } = {}) {
  const normalizedGroupId = String(groupId || "").trim();
  if (!normalizedGroupId) throw new ApiError("groupId is required", 400);
  await apiRequest(`/api/admin/permission-groups/${encodeURIComponent(normalizedGroupId)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
  return { groupId: normalizedGroupId };
}
